import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Shield, Users, Award, ArrowRight, CheckCircle, TrendingUp, Lock, Award as AwardIcon, Zap, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import EMICalculator from '../components/EMICalculator';

const Home = () => {
  const trustFeatures = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: "RBI Registered",
      subtitle: "Registration No: B.01.00449",
      description: "Fully regulated and compliant NBFC"
    },
    {
      icon: <AwardIcon className="h-6 w-6" />,
      title: "29+ Years",
      subtitle: "Since 1996",
      description: "Trusted financial partner"
    },
    {
      icon: <Lock className="h-6 w-6" />,
      title: "100% Secure",
      subtitle: "Bank-Grade Security",
      description: "Your data is completely safe"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "10,000+",
      subtitle: "Happy Customers",
      description: "Trusted by thousands"
    }
  ];

  const features = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: "RBI Registered NBFC",
      description: "Registration No: B.01.00449 - Trusted and regulated financial services with complete transparency."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Expert Team",
      description: "Experienced professionals dedicated to providing personalized financial solutions."
    },
    {
      icon: <Calculator className="h-8 w-8" />,
      title: "Quick Processing",
      description: "Fast loan approval and disbursal with minimal documentation requirements."
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "29+ Years Experience",
      description: "Serving customers since 1996 with consistent growth and customer satisfaction."
    }
  ];

  const loanProducts = [
    {
      title: "Personal Loan",
      description: "Unsecured loans for your personal needs with competitive interest rates.",
      features: ["Attractive Loan Amounts", "Minimal Documentation", "Quick Approval"],
      path: "/personal-loan"
    },
    {
      title: "Home Loan",
      description: "Make your dream home a reality with our flexible home loan solutions.",
      features: ["Competitive Financing", "Attractive Interest Rates", "Long Tenure"],
      path: "/home-loan"
    },
    {
      title: "Loan Against Property",
      description: "Unlock the value of your property with our LAP solutions.",
      features: ["High Loan Amount", "Lower Interest Rates", "Flexible Repayment"],
      path: "/lap"
    },
    {
      title: "Gold Loan",
      description: "Get instant cash against your gold ornaments with hassle-free process.",
      features: ["Fast Approval", "No Income Proof", "Safe Gold Storage"],
      path: "/gold-loan"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23340C7F' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 leading-tight animate-fadeInUp">
                  Your Trusted 
                  <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    Financial Partner
                  </span>
                </h1>
                
                <div className="space-y-2 animate-fadeInUp">
                  <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#340C7F' }}>
                    Pooja Finstock International Limited
                  </h2>
                  <p className="text-lg text-gray-600">PFIL Finance - Since 1996</p>
                </div>
                
                <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed animate-fadeInUp">
                  Since 1996, PFIL Finance has been providing comprehensive financial solutions 
                  to help you achieve your dreams with trust, transparency, and excellence.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fadeInUp">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-1"
                  style={{ backgroundColor: '#340C7F' }}
                >
                  Apply for Loan
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:border-gray-400 hover:text-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Learn More
                </Link>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 text-sm text-gray-600 animate-fadeInUp">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium">RBI Registered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Quick Processing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium">29+ Years Experience</span>
                </div>
              </div>
            </div>

            {/* Right Column - Personal Loan Consultation Image */}
            <div className="relative lg:block">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1659355894117-0ae6f8f28d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjBzdWNjZXNzfGVufDB8fHx8MTc1MzcyOTM0M3ww&ixlib=rb-4.1.0&q=85" 
                  alt="PFIL Finance - Trusted Personal Loans for Indian Professionals" 
                  className="rounded-2xl shadow-2xl w-full h-auto max-w-lg mx-auto animate-float"
                />
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                {/* Overlay badge */}
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: '#340C7F' }}>Personal Loans for Every Indian</div>
                    <div className="text-sm text-gray-600">Trusted financial solutions since 1996</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Credibility Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why <span style={{ color: '#340C7F' }}>10,000+ Customers</span> Trust Us
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Backed by regulatory compliance and decades of excellence in financial services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1 group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-purple-900 transition-colors duration-300">{feature.title}</h3>
                <p className="text-sm font-semibold mb-2 group-hover:text-purple-700 transition-colors duration-300" style={{ color: '#340C7F' }}>{feature.subtitle}</p>
                <p className="text-sm text-gray-600 group-hover:text-purple-600 transition-colors duration-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMI Calculator Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              Calculate Your
              <span className="block" style={{ color: '#340C7F' }}>
                Monthly EMI
              </span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Get instant EMI calculations for all loan types with our advanced calculator. 
              Plan your finances better with accurate projections.
            </p>
          </div>

          {/* Full Width EMI Calculator */}
          <div className="max-w-6xl mx-auto">
            <EMICalculator />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose <span style={{ color: '#340C7F' }}>PFIL Finance?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine decades of experience with modern technology to provide you with the best financial services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center space-y-4 p-8 rounded-2xl hover:shadow-xl transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-gray-50 border border-gray-100">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl text-white shadow-lg" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Products Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Our <span style={{ color: '#340C7F' }}>Loan Products</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our wide range of loan products designed to meet your specific financial needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {loanProducts.map((product, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden hover:scale-105 hover:-translate-y-2 border border-gray-100 group cursor-pointer"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="h-2 group-hover:h-3 transition-all duration-300" style={{ backgroundColor: '#340C7F' }}></div>
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-900 transition-colors duration-300">{product.title}</h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-purple-600 transition-colors duration-300">{product.description}</p>
                  
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600 group-hover:text-purple-600 transition-colors duration-300">
                        <CheckCircle className="h-4 w-4 text-green-500 group-hover:text-green-600 transition-colors duration-300" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={product.path}
                    className="inline-flex items-center text-sm font-semibold hover:underline transition-all duration-300 group-hover:scale-105 transform hover:-translate-y-1"
                    style={{ color: '#340C7F' }}
                  >
                    Learn More
                    <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Banking Features with 3D Image */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Indian Digital Banking Image */}
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_loan-emi-calc/artifacts/1nh8sz82_20250728_2022_PFIL%20Finance%20Mobile%20UI_simple_compose_01k18r7kn1fm3bmpra1ye2y3ms.png" 
                alt="PFIL Finance Mobile App - Personal Loan Application Interface for Indians" 
                className="rounded-2xl shadow-2xl w-full h-auto animate-float"
              />
              {/* Overlay with Indian context stats */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#340C7F' }}>10K+</div>
                    <div className="text-xs text-gray-600">Indian Families</div>
                  </div>
                  <div className="group relative cursor-pointer">
                    <div className="text-2xl font-bold transition-all duration-300 group-hover:opacity-0" style={{ color: '#340C7F' }}>
                      Crores+
                    </div>
                    <div className="absolute inset-0 text-2xl font-bold opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ color: '#340C7F' }}>
                      ₹5 CR
                    </div>
                    <div className="text-xs text-gray-600">Loans Disbursed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold" style={{ color: '#340C7F' }}>24/7</div>
                    <div className="text-xs text-gray-600">Digital Support</div>
                  </div>
                </div>
              </div>
              {/* Indian flag colors accent */}
              <div className="absolute top-4 right-4 flex space-x-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <div className="w-3 h-3 bg-white border border-gray-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
            </div>

            {/* Right Column - Personal Loan Features */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  <span style={{ color: '#340C7F' }}>Personal Loans</span> for Every Indian
                </h2>
                <p className="text-xl text-gray-600">
                  Empowering Indian families with instant personal loans through our digital-first approach and personalized solutions.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-white" 
                       style={{ backgroundColor: '#340C7F' }}>
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Personal Loan Experts</h3>
                    <p className="text-gray-600">Get personalized guidance from our experienced loan advisors who understand Indian financial needs.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-white" 
                       style={{ backgroundColor: '#340C7F' }}>
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Approval</h3>
                    <p className="text-gray-600">Get personal loan approvals in minutes with minimal documentation and hassle-free digital process.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-white" 
                       style={{ backgroundColor: '#340C7F' }}>
                    <Calculator className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Flexible EMI Options</h3>
                    <p className="text-gray-600">Choose from flexible repayment tenures from 12 to 60 months that suit your financial capacity.</p>
                  </div>
                </div>
              </div>

              <Link
                to="/personal-loan"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-xl hover:opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                style={{ backgroundColor: '#340C7F' }}
              >
                Apply for Personal Loan
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Stay <span style={{ color: '#340C7F' }}>Connected</span>
              </h2>
              <p className="text-xl text-gray-600">
                Follow us on social media for the latest updates, financial tips, and success stories from our customers.
              </p>
            </div>
            
            <div className="flex justify-center space-x-8">
              <a 
                href="https://www.facebook.com/profile.php?id=61578619573930" 
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center space-y-2 hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on Facebook"
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full text-white shadow-lg hover:shadow-xl transform group-hover:-translate-y-1 transition-all duration-300"
                     style={{ backgroundColor: '#1877F2' }}>
                  <Facebook className="h-8 w-8" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Facebook</span>
              </a>
              
              <a 
                href="https://x.com/pfilfinance" 
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center space-y-2 hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on X (Twitter)"
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full text-white shadow-lg hover:shadow-xl transform group-hover:-translate-y-1 transition-all duration-300"
                     style={{ backgroundColor: '#000000' }}>
                  <Twitter className="h-8 w-8" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">X (Twitter)</span>
              </a>
              
              <a 
                href="https://www.instagram.com/pfilfinance/" 
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center space-y-2 hover:scale-110 transition-all duration-300"
                aria-label="Follow PFIL Finance on Instagram"
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full text-white shadow-lg hover:shadow-xl transform group-hover:-translate-y-1 transition-all duration-300"
                     style={{ backgroundColor: '#E4405F' }}>
                  <Instagram className="h-8 w-8" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-pink-600 transition-colors">Instagram</span>
              </a>
              
              <a 
                href="https://www.linkedin.com/company/pfilfinance/" 
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center space-y-2 hover:scale-110 transition-all duration-300"
                aria-label="Connect with PFIL Finance on LinkedIn"
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full text-white shadow-lg hover:shadow-xl transform group-hover:-translate-y-1 transition-all duration-300"
                     style={{ backgroundColor: '#0077B5' }}>
                  <Linkedin className="h-8 w-8" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700 transition-colors">LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100">
            Take the first step towards your financial goals. Our team is here to help you every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Apply Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-gray-900 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;